import {
  PatientAISummaryCreate,
  PatientAISummaryDetail,
  PatientAISummaryFeedback,
} from "@/types/patient-ai-summary";

import { requestData } from "@/api/api-config";

/**
 * Get the cached AI summary for a patient.
 * Returns null if no cached summary exists.
 */
export async function getPatientAISummary(
  patientId: string,
  controller?: AbortController,
): Promise<PatientAISummaryDetail | null> {
  try {
    return await requestData<PatientAISummaryDetail>(
      `user/patient/${patientId}/ai-summary`,
      "get",
      {
        signal: controller?.signal,
      },
    );
  } catch {
    // Return null for 404 (no summary exists)
    return null;
  }
}

/**
 * Save or update the AI summary for a patient.
 */
export async function savePatientAISummary(
  patientId: string,
  data: PatientAISummaryCreate,
): Promise<PatientAISummaryDetail | null> {
  return await requestData<PatientAISummaryDetail>(
    `user/patient/${patientId}/ai-summary`,
    "post",
    {
      json: data,
    },
  );
}

/**
 * Submit feedback on an AI summary.
 */
export async function submitAISummaryFeedback(
  patientId: string,
  data: PatientAISummaryFeedback,
): Promise<PatientAISummaryDetail | null> {
  return await requestData<PatientAISummaryDetail>(
    `user/patient/${patientId}/ai-summary/feedback`,
    "post",
    {
      json: data,
    },
  );
}

/**
 * Delete the cached AI summary for a patient.
 * Used to force regeneration of the summary.
 */
export async function deletePatientAISummary(patientId: string): Promise<void> {
  await requestData<void>(`user/patient/${patientId}/ai-summary`, "delete");
}
